(ns ductile.ui.router
  (:require [ductile.ui.deframe :as deframe]
            [ductile.ui.locale :as locale]
            [ductile.ui.routes :as routes]
            [nextjournal.log :as log]
            [reagent.core :as reagent]
            [reitit.core :as reitit]
            [reitit.frontend :as rf]
            [reitit.frontend.easy :as rfe]
            [reitit.frontend.history :as rfh]))

(defonce router (rf/router routes/routes))

(defn routes
  "Get route information as a seq of maps, containing any route data defined in
  ductile.ui.routes, and augmented with a :title based on the localization data
  in ductile.ui.locale."
  []
  (map (fn [[templ route-data]] (assoc route-data
                                       :template templ
                                       :title (or (locale/tr (:name route-data))
                                                  (str (:name route-data)))))
       (reitit/routes router)))

(defn hard-refresh-to [new-path]
  (.replace js/window.location new-path))

(defn current-path []
  (.-pathname js/window.location))

;; When non-nil, navigation will prompt for confirmation with this message.
;; Also installs a beforeunload handler so the browser confirms tab close/refresh.
(defonce !navigation-guard (atom nil))

(defn set-navigation-guard!
  "Set the navigation guard message used for in-app navigation and beforeunload prompts."
  [msg]
  (reset! !navigation-guard msg))

(defonce _navigation-guard-beforeunload
  (let [handler (fn [e]
                  (when @!navigation-guard
                    (.preventDefault e)
                    (set! (.-returnValue e) "")))]
    (.addEventListener js/window "beforeunload" handler)))

(defn ^:export navigate-to
  "Navigate to the given path, then trigger routing."
  {::deframe/effect true}
  [path]
  (when path
    (.pushState js/window.history nil "" path)
    (rfh/-on-navigate @rfe/history path)))

(defn current-route
  "Get the currently active route, returns match data, configured route data is
  under the :data key."
  []
  ;; Allow loading routes with or without slash.
  ;; There's probably a better way to do this.
  (or (rf/match-by-path router (rfh/-get-path @rfe/history))
      (rf/match-by-path router (str (rfh/-get-path @rfe/history) "/"))))

(defn name+params
  "Given a path, return the route's name and params"
  [path]
  ((juxt (comp :name :data) :path-params) (rf/match-by-path router path)))

(defn current-route-template []
  (let [{:as route :keys [path data path-params template]} (current-route)]
    {:path-params path-params
     :template (routes/path->template template)
     :data data}))

(defn same-route-with [new-params]
  (let [{:keys [template path-params data]} (current-route-template)]
    (routes/path template (merge path-params new-params))))

(defn same-route-different-compound [compound-name]
  (let [{:keys [template path-params data]} (current-route-template)]
    (when (or (contains? path-params :compound-name) (contains? path-params :compound/name))
      (if (or (= template '[compound :compound-name vehicle :fahrzeug-id])
              (and (set? (:enabled-compounds data))
                   (not (contains? (:enabled-compounds data) compound-name))))
        "/"
        (routes/path template (assoc path-params :compound-name compound-name :compound/name compound-name))))))

(def path routes/path)
(def build-nav routes/build-nav)
(def path->template routes/path->template)

(def !access-ctx
  "returns the `access-ctx` for `can-access?`"
  (reagent/track (fn []
                   (assoc (:path-params @deframe/!current-route)
                          :user @deframe/!current-user))))

(defn init [{:keys [use-fragment?]}]
  (rfe/start!
   router
   (fn [new-match]
     (if-let [msg @!navigation-guard]
       (if (js/confirm msg)
         (do
           (log/trace :routing-to new-match)
           (deframe/dispatch! 'ductile.ui.deframe/current-route-changed new-match))
         ;; Restore previous URL since pushState already happened
         (when-let [prev (:path @deframe/!current-route)]
           (.replaceState js/window.history nil "" prev)))
       (do
         (log/trace :routing-to new-match)
         (deframe/dispatch! 'ductile.ui.deframe/current-route-changed new-match))))
   {:use-fragment use-fragment?}))
